import { Fragment, useMemo, useEffect, useRef, useState } from 'react';
import type { OrderDto, OrderItemDto, OrdersListFilterMeta, OrdersListParams } from '@/services/orders.service';
import { ordersService } from '@/services/orders.service';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminRangeFilter } from '@/components/AdminRangeFilter/AdminRangeFilter';
import AdminCheckboxFilter from '@/components/AdminCheckboxFilter/AdminCheckboxFilter';
import { AdminDateFilter } from '@/components/AdminDateFilter/AdminDateFilter';
import { useNumericRangeFilter } from '@/hooks/useNumericRangeFilter';
import { AdminOrderDetailsPanel } from '@/components/AdminOrderDetailsPanel';
import { downloadOrdersCsv } from '@/services/orders-export.service';
import { useAuthStore } from '@/store/auth.store';
import { useOrdersStore } from '@/store/orders.store';
import { formatDateOnlyForDisplay } from '@/utils/date-only';
import { getPromoDiscountAmountInCents } from '@/utils/order-pricing';
import { copyTextToClipboard } from '@/utils/clipboard';
import styles from './AdminOrdersTable.module.css';
import skipassIcon from '@/assets/icons/skipass.svg';
import expandIcon from '@/assets/icons/expand.svg';
import copyGrayIcon from '@/assets/icons/copy_gray.svg';
import emptyOrdersIcon from '@/assets/icons/empty-orders.svg';
import downloadIcon from '@/assets/icons/download.svg';

type OrdersQueryFilters = {
  passMin?: number;
  passMax?: number;
  skiDaysMin?: number;
  skiDaysMax?: number;
  createdFrom?: string;
  createdTo?: string;
  paidFrom?: string;
  paidTo?: string;
  statuses?: string[];
  domains?: string[];
};

const ORDERS_TABLE_FILTERS_STORAGE_KEY = 'admin.ordersTable.filters.v1';

const sanitizeStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const next = value
    .filter((entry): entry is string => typeof entry === 'string')
    .map((entry) => entry.trim())
    .filter(Boolean);

  return next.length ? next : undefined;
};

const sanitizeNumber = (value: unknown): number | undefined => {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

const sanitizeDateString = (value: unknown): string | undefined => {
  return typeof value === 'string' && value.trim() ? value : undefined;
};

const sanitizeQueryFilters = (value: unknown): OrdersQueryFilters => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  const input = value as Record<string, unknown>;

  return {
    passMin: sanitizeNumber(input.passMin),
    passMax: sanitizeNumber(input.passMax),
    skiDaysMin: sanitizeNumber(input.skiDaysMin),
    skiDaysMax: sanitizeNumber(input.skiDaysMax),
    createdFrom: sanitizeDateString(input.createdFrom),
    createdTo: sanitizeDateString(input.createdTo),
    paidFrom: sanitizeDateString(input.paidFrom),
    paidTo: sanitizeDateString(input.paidTo),
    statuses: sanitizeStringArray(input.statuses),
    domains: sanitizeStringArray(input.domains),
  };
};

const getInitialPersistedFilters = (): { searchQuery: string; queryFilters: OrdersQueryFilters } => {
  if (typeof window === 'undefined') {
    return { searchQuery: '', queryFilters: {} };
  }

  const raw = window.sessionStorage.getItem(ORDERS_TABLE_FILTERS_STORAGE_KEY);
  if (!raw) {
    return { searchQuery: '', queryFilters: {} };
  }

  try {
    const parsed = JSON.parse(raw) as { searchQuery?: unknown; queryFilters?: unknown };
    return {
      searchQuery: typeof parsed.searchQuery === 'string' ? parsed.searchQuery : '',
      queryFilters: sanitizeQueryFilters(parsed.queryFilters),
    };
  } catch {
    return { searchQuery: '', queryFilters: {} };
  }
};

const formatDate = (value?: string | null) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '-'
    : date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
};

const formatDateOnly = (value?: string | null) => {
  return formatDateOnlyForDisplay(value, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatMoney = (value?: { amount: number; currency: string } | null) => {
  if (!value) {
    return '-';
  }

  const amount = Number.isFinite(value.amount) ? value.amount : 0;
  const currency = value.currency || '';
  return `${(amount/100).toFixed(2)} ${currency}`.trim();
};

const formatCardMoney = (amountInCents: number, currency: string) => {
  const amount = amountInCents / 100;
  const formatted = Math.round(amount)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return `${formatted} ${currency}`;
};

const formatMarginPercent = (value?: number) => {
  if (!Number.isFinite(value)) {
    return null;
  }

  return `${Math.trunc(value ?? 0)}%`;
};

const formatPassCategoryLabel = (rawCategory: string) => {
  const normalized = rawCategory.trim().toLowerCase();

  if (normalized === 'adult') {
    return 'Adults';
  }

  if (normalized === 'child' || normalized === 'children') {
    return 'Children';
  }

  const capitalized = normalized.charAt(0).toUpperCase() + normalized.slice(1);
  return capitalized.endsWith('s') ? capitalized : `${capitalized}s`;
};

const getPassBreakdown = (order: OrderDto): Array<{ label: string; quantity: number }> => {
  const aggregated = new Map<string, number>();

  const addEntry = (category: string, quantity: number) => {
    if (!category || quantity <= 0) {
      return;
    }

    const label = formatPassCategoryLabel(category);
    aggregated.set(label, (aggregated.get(label) ?? 0) + quantity);
  };

  if (order.items.length) {
    order.items.forEach((item) => {
      addEntry(item.ageCategory, 1);
    })
  } else if (order.passSelections?.length) {
    order.passSelections.forEach((selection) => {
      addEntry(selection.ageCategory, selection.quantity);
    }) } else if (order.tripDetails?.skierCounts?.length) {
    order.tripDetails.skierCounts.forEach((count) => {
      addEntry(count.category, count.quantity);
    });
  }

  return Array.from(aggregated.entries()).map(([label, quantity]) => ({ label, quantity }));
};

const getOrderPassCount = (order: OrderDto): number => {
  if (order.items.length) {
    return order.items.length;
  }

  if (order.passSelections?.length) {
    return order.passSelections.reduce((sum, selection) => sum + Math.max(0, selection.quantity), 0);
  }

  if (order.tripDetails?.skierCounts?.length) {
    return order.tripDetails.skierCounts.reduce((sum, entry) => sum + Math.max(0, entry.quantity), 0);
  }

  return Number.isFinite(order.passes) && order.passes > 0 ? order.passes : 0;
};

const toSafeNumber = (value: number) => (Number.isFinite(value) ? value : 0);

const statusClassNames: Record<string, string | undefined> = {
  draft: styles.badgeDraft,
  created: styles.badgeDraft,
  paid: styles.badgePaid,
  confirmed: styles.badgeConfirmed,
  cancelled: styles.badgeCancelled,
  refunded: styles.badgeRefunded,
};

export const AdminOrdersTable = () => {
  const navigate = useNavigate();
  const { orderId: orderIdParam } = useParams<{ orderId?: string }>();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const orders = useOrdersStore((state) => state.orders);
  const totalOrdersCount = useOrdersStore((state) => state.total);
  const ordersStats = useOrdersStore((state) => state.stats);
  const filterMeta = useOrdersStore((state) => state.filterMeta) as OrdersListFilterMeta | null;
  const isLoading = useOrdersStore((state) => state.isLoading);
  const error = useOrdersStore((state) => state.error);
  const fetchOrders = useOrdersStore((state) => state.fetchOrders);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [overlayLeft, setOverlayLeft] = useState(0);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [fallbackSelectedOrder, setFallbackSelectedOrder] = useState<OrderDto | null>(null);
  const initialPersistedFilters = useMemo(() => getInitialPersistedFilters(), []);
  const [searchQuery, setSearchQuery] = useState(initialPersistedFilters.searchQuery);
  const [copyToast, setCopyToast] = useState<{ visible: boolean; x: number; y: number }>({
    visible: false,
    x: 0,
    y: 0,
  });
  const copyResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [domainOptions, setDomainOptions] = useState<Array<{ key: string; label: string }>>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [queryFilters, setQueryFilters] = useState<OrdersQueryFilters>(
    initialPersistedFilters.queryFilters
  );
  const [debouncedQueryFilters, setDebouncedQueryFilters] = useState(queryFilters);
  const STATUS_OPTIONS: Array<{ key: string; label: string }> = [
    { key: 'draft', label: 'Draft' },
    { key: 'created', label: 'Created' },
    { key: 'paid', label: 'Paid' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'cancelled', label: 'Cancelled' },
  ];

  const selectedOrderId = useMemo(() => {
    if (!orderIdParam) {
      return null;
    }

    const parsed = Number.parseInt(orderIdParam, 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return null;
    }

    return parsed;
  }, [orderIdParam]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQueryFilters(queryFilters);
    }, 250);

    return () => {
      window.clearTimeout(timer);
    };
  }, [queryFilters]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.setItem(
      ORDERS_TABLE_FILTERS_STORAGE_KEY,
      JSON.stringify({
        searchQuery,
        queryFilters,
      })
    );
  }, [queryFilters, searchQuery]);

  useEffect(() => {
    let isCancelled = false;

    void ordersService
      .listDomainSuggestions('', 20)
      .then((names) => {
        if (isCancelled) return;
        setDomainOptions(
          names
            .map((name) => name.trim())
            .filter(Boolean)
            .map((name) => ({ key: name, label: name }))
        );
      })
      .catch(() => {
        if (isCancelled) return;
        setDomainOptions([]);
      });

    return () => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!orderIdParam) {
      return;
    }

    if (selectedOrderId == null) {
      void navigate('/admin', { replace: true });
    }
  }, [navigate, orderIdParam, selectedOrderId]);

  const passCounts = useMemo(() => orders.map(getOrderPassCount), [orders]);
  const skiDaysValues = useMemo(() => orders.map((order) => toSafeNumber(order.skiDays)), [orders]);

  const passBounds = filterMeta?.passes ?? {
    min: passCounts.length ? Math.min(...passCounts) : 0,
    max: passCounts.length ? Math.max(...passCounts) : 0,
  };

  const skiDaysBounds = filterMeta?.skiDays ?? {
    min: skiDaysValues.length ? Math.min(...skiDaysValues) : 0,
    max: skiDaysValues.length ? Math.max(...skiDaysValues) : 0,
  };

  const passFilter = useNumericRangeFilter(passCounts, {
    min: passBounds.min,
    max: passBounds.max,
  });
  const skiDaysFilter = useNumericRangeFilter(skiDaysValues, {
    min: skiDaysBounds.min,
    max: skiDaysBounds.max,
  });

  const applyQueryFilters = (
    updater: (prev: typeof queryFilters) => typeof queryFilters
  ) => {
    setQueryFilters((prev) => {
      const next = updater(prev);

      const arraysEqual = (a?: string[], b?: string[]) => {
        if (a === b) return true;
        if (!a && !b) return true;
        if (!a || !b) return false;
        if (a.length !== b.length) return false;
        const sa = [...a].sort();
        const sb = [...b].sort();
        for (let i = 0; i < sa.length; i++) {
          if (sa[i] !== sb[i]) return false;
        }
        return true;
      };

      if (
        prev.passMin === next.passMin &&
        prev.passMax === next.passMax &&
        prev.skiDaysMin === next.skiDaysMin &&
        prev.skiDaysMax === next.skiDaysMax &&
        prev.createdFrom === next.createdFrom &&
        prev.createdTo === next.createdTo &&
        prev.paidFrom === next.paidFrom &&
        prev.paidTo === next.paidTo &&
        arraysEqual(prev.statuses, next.statuses) &&
        arraysEqual(prev.domains, next.domains)
      ) {
        return prev;
      }

      setPage(1);
      return next;
    });
  };

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const trimmedQuery = searchQuery.trim().toLowerCase();
    void fetchOrders({
      page,
      limit,
      reference: trimmedQuery || undefined,
      passMin: debouncedQueryFilters.passMin,
      passMax: debouncedQueryFilters.passMax,
      skiDaysMin: debouncedQueryFilters.skiDaysMin,
      skiDaysMax: debouncedQueryFilters.skiDaysMax,
      createdFrom: debouncedQueryFilters.createdFrom,
      createdTo: debouncedQueryFilters.createdTo,
      paidFrom: debouncedQueryFilters.paidFrom,
      paidTo: debouncedQueryFilters.paidTo,
      statuses: debouncedQueryFilters.statuses,
      domains: debouncedQueryFilters.domains,
    });
  }, [
    debouncedQueryFilters.createdFrom,
    debouncedQueryFilters.createdTo,
    debouncedQueryFilters.domains,
    debouncedQueryFilters.paidFrom,
    debouncedQueryFilters.paidTo,
    debouncedQueryFilters.passMax,
    debouncedQueryFilters.passMin,
    debouncedQueryFilters.skiDaysMax,
    debouncedQueryFilters.skiDaysMin,
    debouncedQueryFilters.statuses,
    fetchOrders,
    isAuthenticated,
    limit,
    page,
    searchQuery,
  ]);

  useEffect(() => {
    const updateOverlayLeft = () => {
      if (!wrapperRef.current) return;
      setOverlayLeft(wrapperRef.current.scrollLeft + wrapperRef.current.clientWidth / 2);
    };

    updateOverlayLeft();
    window.addEventListener('resize', updateOverlayLeft);
    return () => {
      window.removeEventListener('resize', updateOverlayLeft);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  const handleReferenceCopy = async (
    reference: string,
    position?: { x: number; y: number }
  ) => {
    const copied = await copyTextToClipboard(reference);
    if (!copied) {
      return;
    }

    const fallbackPosition = {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    };

    setCopyToast({
      visible: true,
      x: position?.x ?? fallbackPosition.x,
      y: position?.y ?? fallbackPosition.y,
    });

    if (copyResetTimeoutRef.current) {
      clearTimeout(copyResetTimeoutRef.current);
    }
    copyResetTimeoutRef.current = setTimeout(() => {
      setCopyToast((prev) => ({ ...prev, visible: false }));
      copyResetTimeoutRef.current = null;
    }, 1500);
  };

  const handleStatusChange = (nextStatuses: string[]) => {
    applyQueryFilters((prev) => ({ ...prev, statuses: nextStatuses.length ? nextStatuses : undefined }));
  };

  const handleDomainChange = (nextDomains: string[]) => {
    applyQueryFilters((prev) => ({ ...prev, domains: nextDomains.length ? nextDomains : undefined }));
  };

  const handleDateChange = (filterName: 'created' | 'paid') => (nextValue: { from?: string; to?: string }) => {
    const prefix = filterName === 'created' ? 'created' : 'paid';

    applyQueryFilters((prev) => ({
      ...prev,
      [`${prefix}From`]: nextValue.from,
      [`${prefix}To`]: nextValue.to,
    }));
  };

  const toggleExpanded = (orderId: number) => {
    setExpanded((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  function ensureOrderItems(order: OrderDto) {
    if (order.items.length) {
      return;
    }

    if (order.passSelections?.length) {
      order.items = order.passSelections.flatMap((selection) => {
        const category = selection.ageCategory;
        const fakeItems = [];
        for (let i = 0; i < selection.quantity; i++) {
          fakeItems.push({
            skipassId: 0,
            ageCategory: category,
            birthDate: '',
            firstName: '',
            lastName: '',
            startDate: order.startDate,
            days: order.skiDays,
            domain: selection.domain,
            supplierPrice: { amount: 0, currency: '' },
            skiPassPrice: { amount: 0, currency: '' },
            margin: { amount: 0, currency: '' },
          } as OrderItemDto);
        }
        return fakeItems;
      });
      return;
    }

    if (order.tripDetails) {
      order.items = order.tripDetails.skierCounts.flatMap((data) => {
        const category = data.category[0]?.toUpperCase() + data.category.slice(1) || 'Unknown';
        const fakeItems = [];
        for (let i = 0; i < data.quantity; i++) {
          fakeItems.push({
            skipassId: 0,
            ageCategory: category,
            birthDate: '',
            firstName: '',
            lastName: '',
            startDate: order.startDate,
            days: order.skiDays,
            domain: order.domains[0] ?? { id: 0, name: '' },
            supplierPrice: { amount: 0, currency: '' },
            skiPassPrice: { amount: 0, currency: '' },
            margin: { amount: 0, currency: '' },
          } as OrderItemDto);
        }
        return fakeItems;
      });
      return;
    }

    console.error('Order expanded but no details found for order', order.orderId);
  }

  useEffect(() => {
    if (selectedOrderId == null) {
      setFallbackSelectedOrder(null);
      return;
    }

    const existingOrder = orders.find((order) => order.orderId === selectedOrderId);
    if (existingOrder) {
      setFallbackSelectedOrder(null);
      return;
    }

    let isCancelled = false;

    void ordersService
      .getById(selectedOrderId)
      .then((order) => {
        if (isCancelled) {
          return;
        }

        ensureOrderItems(order);
        setFallbackSelectedOrder(order);
      })
      .catch(() => {
        if (isCancelled) {
          return;
        }

        setFallbackSelectedOrder(null);
      });

    return () => {
      isCancelled = true;
    };
  }, [orders, selectedOrderId]);

  const handleToggleExpanded = (order: OrderDto, isExpanded: boolean) => {
    const willExpand = !isExpanded;

    if (willExpand) {
      ensureOrderItems(order);
    }

    toggleExpanded(order.orderId);
  };

  const handleOpenOrderPanel = (order: OrderDto) => {
    ensureOrderItems(order);
    void navigate(`/admin/orders/${String(order.orderId)}`);
  };

  const handleCloseOrderPanel = () => {
    void navigate('/admin');
  };

  const handleOrderCancelled = async () => {
    const trimmedQuery = searchQuery.trim().toLowerCase();
    await fetchOrders({
      page,
      limit,
      reference: trimmedQuery || undefined,
      passMin: debouncedQueryFilters.passMin,
      passMax: debouncedQueryFilters.passMax,
      skiDaysMin: debouncedQueryFilters.skiDaysMin,
      skiDaysMax: debouncedQueryFilters.skiDaysMax,
      createdFrom: debouncedQueryFilters.createdFrom,
      createdTo: debouncedQueryFilters.createdTo,
      paidFrom: debouncedQueryFilters.paidFrom,
      paidTo: debouncedQueryFilters.paidTo,
      statuses: debouncedQueryFilters.statuses,
      domains: debouncedQueryFilters.domains,
    });
  };

  const rows = useMemo(() => orders, [orders]);
  const selectedOrderInRows = useMemo(
    () => rows.find((order) => order.orderId === selectedOrderId) ?? null,
    [rows, selectedOrderId]
  );
  const selectedOrder = selectedOrderInRows ?? fallbackSelectedOrder;

  const onChangeCallback = (filterName: 'pass' | 'skiDays') => (nextValue: { min: number; max: number }) => {
    const isPassFilter = filterName === 'pass';
    (isPassFilter ? passFilter.setValue : skiDaysFilter.setValue)(nextValue);
    const bounds = isPassFilter ? passFilter.bounds : skiDaysFilter.bounds;
    const isActive = nextValue.min > bounds.min || nextValue.max < bounds.max;

    applyQueryFilters((prev) => ({
      ...prev,
      [`${filterName}Min`]: isActive ? nextValue.min : undefined,
      [`${filterName}Max`]: isActive ? nextValue.max : undefined,
    }));
  }

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      Boolean(searchQuery.trim()) ||
      queryFilters.passMin != null ||
      queryFilters.passMax != null ||
      queryFilters.skiDaysMin != null ||
      queryFilters.skiDaysMax != null ||
      queryFilters.createdFrom ||
      queryFilters.createdTo ||
      queryFilters.paidFrom ||
      queryFilters.paidTo ||
      (queryFilters.statuses && queryFilters.statuses.length > 0) ||
      (queryFilters.domains && queryFilters.domains.length > 0)
    );
  }, [queryFilters, searchQuery]);

  const clearAllFilters = () => {
    passFilter.setValue({ min: passFilter.bounds.min, max: passFilter.bounds.max });
    skiDaysFilter.setValue({ min: skiDaysFilter.bounds.min, max: skiDaysFilter.bounds.max });
    setPage(1);
    setQueryFilters({
      passMin: undefined,
      passMax: undefined,
      skiDaysMin: undefined,
      skiDaysMax: undefined,
      createdFrom: undefined,
      createdTo: undefined,
      paidFrom: undefined,
      paidTo: undefined,
      statuses: undefined,
      domains: undefined,
    });
    setSearchQuery('');
  };

  const totalPages = Math.max(1, Math.ceil(totalOrdersCount / limit));
  const canGoPrevPage = page > 1;
  const canGoNextPage = page < totalPages;
  const visibleFrom = totalOrdersCount === 0 ? 0 : (page - 1) * limit + 1;
  const visibleTo = Math.min(page * limit, totalOrdersCount);

  const selectedOrderStatusClass = selectedOrder
    ? statusClassNames[selectedOrder.status] ?? statusClassNames.created ?? ''
    : '';
  const selectedOrderStatusLabel = selectedOrder
    ? selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)
    : '';
  const selectedOrderPassCount = selectedOrder ? getOrderPassCount(selectedOrder) : 0;
  const selectedOrderDomainNames = selectedOrder?.domains.length
    ? selectedOrder.domains.map((domain) => domain.name).join(', ')
    : '-';

  const stats = useMemo(() => {
    const totalRevenueInCents = ordersStats?.totalRevenueInCents ?? 0;
    const totalMarginInCents = ordersStats?.totalMarginInCents ?? 0;
    const soldPasses = ordersStats?.soldPasses ?? 0;
    const totalSoldOrders = ordersStats?.totalSoldOrders ?? 0;
    const avgPassPriceInCents = ordersStats?.avgPassPriceInCents ?? 0;
    const avgPassDays = ordersStats?.avgPassDays ?? 0;
    const totalOrders = ordersStats?.totalOrders ?? totalOrdersCount;
    const currency = ordersStats?.currency || orders[0]?.clientPrice.currency || 'EUR';

    return {
      totalRevenue: formatCardMoney(totalRevenueInCents, currency),
      totalMargin: formatCardMoney(totalMarginInCents, currency),
      soldPasses,
      avgPassPrice: formatCardMoney(avgPassPriceInCents, currency),
      avgPassDays: Number.isInteger(avgPassDays)
        ? String(avgPassDays)
        : avgPassDays.toFixed(1).replace(/\.0$/, ''),
      totalOrders,
      totalSoldOrders,
    };
  }, [orders, ordersStats, totalOrdersCount]);

  const handleExportFilteredOrders = async () => {
    const hasServerFilter =
      Boolean(searchQuery.trim()) ||
      queryFilters.passMin != null ||
      queryFilters.passMax != null ||
      queryFilters.skiDaysMin != null ||
      queryFilters.skiDaysMax != null ||
      queryFilters.createdFrom != null ||
      queryFilters.createdTo != null ||
      queryFilters.paidFrom != null ||
      queryFilters.paidTo != null ||
      (queryFilters.statuses != null && queryFilters.statuses.length > 0) ||
      (queryFilters.domains != null && queryFilters.domains.length > 0);

    const exportFilters: OrdersListParams = {
      reference: searchQuery.trim().toLowerCase() || undefined,
      passMin: debouncedQueryFilters.passMin,
      passMax: debouncedQueryFilters.passMax,
      skiDaysMin: debouncedQueryFilters.skiDaysMin,
      skiDaysMax: debouncedQueryFilters.skiDaysMax,
      createdFrom: debouncedQueryFilters.createdFrom,
      createdTo: debouncedQueryFilters.createdTo,
      paidFrom: debouncedQueryFilters.paidFrom,
      paidTo: debouncedQueryFilters.paidTo,
      statuses: debouncedQueryFilters.statuses,
      domains: debouncedQueryFilters.domains,
    };

    try {
      setIsExporting(true);
      const allFilteredOrders = await ordersService.listAll(exportFilters);
      downloadOrdersCsv(allFilteredOrders, { isFiltered: hasServerFilter });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className={styles.ordersSection}>
      <div className={styles.statsGrid}>
        <div className={`${styles.statCard} ${styles.statCardBlue}`}>
          <p className={styles.statLabel}>Total income</p>
          <p className={styles.statValue}>{stats.totalRevenue}</p>
        </div>
        <div className={`${styles.statCard} ${styles.statCardBlue}`}>
          <p className={styles.statLabel}>Total margin</p>
          <p className={styles.statValue}>{stats.totalMargin}</p>
        </div>
        <div className={`${styles.statCard} ${styles.statCardSuccess}`}>
          <p className={styles.statLabel}>Sold ski passes</p>
          <p className={styles.statValue}>{stats.soldPasses}</p>
        </div>
        <div className={`${styles.statCard} ${styles.statCardWarning}`}>
          <p className={styles.statLabel}>Avg pass price</p>
          <p className={styles.statValue}>{stats.avgPassPrice}</p>
        </div>
        <div className={`${styles.statCard} ${styles.statCardDanger}`}>
          <p className={styles.statLabel}>Avg pass days</p>
          <p className={styles.statValue}>{stats.avgPassDays}</p>
        </div>
        <div className={`${styles.statCard} ${styles.statCardSurface}`}>
          <p className={styles.statLabel}>Total orders</p>
          <p className={styles.statValue}>{stats.totalOrders}</p>
        </div>
        <div className={`${styles.statCard} ${styles.statCardSurface}`}>
          <p className={styles.statLabel}>Total sold orders</p>
          <p className={styles.statValue}>{stats.totalSoldOrders}</p>
        </div>
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}

      <div className={styles.controls}>
        <div className={styles.searchBox}>
          <span className={styles.searchIcon}>⌕</span>
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => {
              setPage(1);
              setSearchQuery(event.target.value);
            }}
            placeholder="Search by reference..."
            className={styles.searchInput}
          />
        </div>

        <div className={styles.controlsActions}>
          <div className={styles.paginationControls}>
            <span className={styles.paginationSummary}>
              {visibleFrom}-{visibleTo} of {totalOrdersCount}
            </span>
            <label className={styles.limitSelectLabel}>
              Show
              <select
                className={styles.limitSelect}
                value={String(limit)}
                onChange={(event) => {
                  const nextLimit = Number.parseInt(event.target.value, 10);
                  if (!Number.isFinite(nextLimit) || nextLimit <= 0) {
                    return;
                  }

                  setLimit(nextLimit);
                  setPage(1);
                }}
              >
                {[50, 100, 200].map((option) => (
                  <option key={option} value={String(option)}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className={styles.paginationButton}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={!canGoPrevPage || isLoading}
            >
              Prev
            </button>
            <span className={styles.paginationPage}>
              Page {page} / {totalPages}
            </span>
            <button
              type="button"
              className={styles.paginationButton}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={!canGoNextPage || isLoading}
            >
              Next
            </button>
          </div>

          <button
            type="button"
            className={styles.saveButton}
            onClick={() => {
              void handleExportFilteredOrders();
            }}
            disabled={isExporting}
          >
            <img src={downloadIcon} alt="Download" />
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>

      <div className={styles.tableSection}>
        {isLoading ? <span className={styles.loading}>Loading…</span> : null}
        <div
          className={styles.wrapper}
          ref={wrapperRef}
          onScroll={() => {
            if (!wrapperRef.current) return;
            setOverlayLeft(wrapperRef.current.scrollLeft + wrapperRef.current.clientWidth / 2);
          }}
        >
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={`${styles.th} ${styles.stickyCol1} ${styles.stickyHead} ${styles.colExpand}`} />
                <th className={`${styles.th} ${styles.stickyCol2} ${styles.stickyHead} ${styles.colId}`}>ID</th>
                <th className={`${styles.th} ${styles.stickyCol3} ${styles.stickyHead} ${styles.colReference}`}>Reference</th>
                <th className={`${styles.th} ${styles.thFilterable} ${styles.colPasses}`}>
                  <AdminRangeFilter
                    label="Passes"
                    bounds={passFilter.bounds}
                    value={passFilter.value}
                    histogramValues={passCounts}
                    histogramBars={filterMeta?.passes.bars}
                    isActive={queryFilters.passMin != null || queryFilters.passMax != null}
                    onChange={onChangeCallback('pass')}
                  />
                </th>
                <th className={`${styles.th} ${styles.colSkiDays}`}>
                  <AdminRangeFilter
                    label="Ski Days"
                    bounds={skiDaysFilter.bounds}
                    value={skiDaysFilter.value}
                    histogramValues={skiDaysValues}
                    histogramBars={filterMeta?.skiDays.bars}
                    isActive={queryFilters.skiDaysMin != null || queryFilters.skiDaysMax != null}
                    onChange={onChangeCallback('skiDays')}
                  />
                </th>
                <th className={`${styles.th} ${styles.colStartDate}`}>Start Date</th>
                <th className={`${styles.th} ${styles.colDomain}`}>
                  <AdminCheckboxFilter
                    label="Domain"
                    options={domainOptions}
                    value={queryFilters.domains ?? []}
                    onChange={handleDomainChange}
                    searchable={{
                      placeholder: 'Search domain...',
                      minQueryLength: 2,
                      debounceMs: 250,
                      loadOptions: async (query, signal) => {
                        const names = await ordersService.listDomainSuggestions(query, 30);
                        if (signal.aborted) {
                          return [];
                        }
                        return names.map((name) => ({ key: name, label: name }));
                      },
                    }}
                  />
                </th>
                <th className={styles.th}>
                  <AdminCheckboxFilter
                    label="Status"
                    options={STATUS_OPTIONS}
                    value={queryFilters.statuses ?? []}
                    onChange={handleStatusChange}
                  />
                </th>
                <th className={styles.th}>Supplier Price</th>
                <th className={styles.th}>Client Price</th>
                <th className={styles.th}>Applied<br/>Margin %</th>
                <th className={styles.th}>Promo code</th>
                <th className={styles.th}>Discount</th>
                <th className={styles.th}>Margin</th>
                <th className={styles.th}>Applied<br/>Loyalty pts.</th>
                <th className={styles.th}>Refund</th>
                <th className={styles.th}>Email</th>
                <th className={styles.th}>
                  <AdminDateFilter
                    label="Created At"
                    value={{ from: queryFilters.createdFrom, to: queryFilters.createdTo }}
                    onChange={handleDateChange('created')}
                  />
                </th>
                <th className={styles.th}>
                  <AdminDateFilter
                    label="Paid At"
                    value={{ from: queryFilters.paidFrom, to: queryFilters.paidTo }}
                    onChange={handleDateChange('paid')}
                  />
                </th>
                <th className={`${styles.th} ${styles.stickyLast} ${styles.stickyHead}`} />
              </tr>
            </thead>
            <tbody>
              {rows.map((order) => {
                const statusClass = statusClassNames[order.status] ?? statusClassNames.created ?? '';
                const isExpanded = expanded[order.orderId] ?? false;
                // const minSkiDays =
                // const maxSkiDays =
                let domainNames = order.domains.map((d) => d.name).join(', ') ;
                domainNames = domainNames.length > 33 ? `${domainNames.slice(0, 30)}…` : domainNames;
                const promoCodeTrimmed = !order.promoCode ? '' : order.promoCode.length > 15 ? `${order.promoCode.slice(0, 12)}…` : order.promoCode;
                const orderStatus = order.status.charAt(0).toUpperCase() + order.status.slice(1);
                const passCount = getOrderPassCount(order);
                const passBreakdown = getPassBreakdown(order);
                const discountAmount = getPromoDiscountAmountInCents(order);
                const discountCurrency = order.clientPrice?.currency || order.supplierPrice?.currency || '';
                const fnExpandRow = () => handleToggleExpanded(order, isExpanded);

                return (
                  <Fragment key={order.orderId}>
                    <tr key={order.orderId} className={isExpanded ? styles.rowExpanded : ''}>
                      <td
                        className={`${styles.tdIcon} ${styles.stickyCol1} ${styles.colExpand}`}
                        onClick={fnExpandRow}
                      >
                        <button
                          type="button"
                          className={`${styles.rowExpandButton} ${isExpanded ? styles.rowExpandButtonExpanded : ''}`}
                          aria-label={isExpanded ? 'Collapse order details' : 'Expand order details'}
                        >
                          {isExpanded ? '−' : '+'}
                        </button>
                      </td>
                      <td
                        className={`${styles.td} ${styles.tdId} ${styles.stickyCol2} ${styles.colId}`}
                        onClick={fnExpandRow}
                      >
                        {order.orderId}
                      </td>
                      <td
                        className={`${styles.td} ${styles.stickyCol3} ${styles.colReference}`}
                      >
                        {order.reference ? (
                          <button
                            type="button"
                            className={styles.referenceButton}
                            onClick={(event) =>
                              void handleReferenceCopy(order.reference, {
                                x: event.clientX,
                                y: event.clientY,
                              })
                            }
                            title="Copy reference"
                            aria-label={`Copy reference ${order.reference}`}
                          >
                            <span className={styles.referenceValue}>{order.reference}</span>
                            <img src={copyGrayIcon} alt="" aria-hidden className={styles.referenceCopyIcon} />
                          </button>
                        ) : (
                          <span className={styles.emptyValue} />
                        )}
                      </td>
                      <td className={`${styles.td} ${styles.passesTd} ${styles.colPasses}`}>
                        <div className={styles.passesTooltipWrapper}>
                          <span className={styles.passesPill}>
                            {passCount}
                            <img src={skipassIcon} alt="Skipass" className={styles.skipassIcon} />
                          </span>
                          {passBreakdown.length ? (
                            <div className={styles.passesTooltip} role="tooltip" aria-hidden>
                              {passBreakdown.map((entry) => (
                                <div key={entry.label} className={styles.passesTooltipRow}>
                                  <span className={styles.passesTooltipQty}>{entry.quantity}x</span>
                                  <span className={styles.passesTooltipLabel}>{entry.label}</span>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </td>
                      <td className={`${styles.td} ${styles.colSkiDays}`}>{order.skiDays}</td>
                      <td className={`${styles.td} ${styles.colStartDate}`}>{formatDateOnly(order.startDate)}</td>
                      <td className={`${styles.td} ${styles.colDomain}`}>
                        {order.domains.length > 0 ? domainNames : '-'}
                      </td>
                      <td className={styles.td}>
                        <span className={`${styles.badge} ${statusClass}`}>
                          {orderStatus}
                        </span>
                      </td>
                      <td className={`${styles.td} ${styles.tdMuted}`}>{order.supplierPrice.amount ? formatMoney(order.supplierPrice) : (
                          <span className={styles.emptyValue}/>
                        )}</td>
                      <td className={styles.td}>{order.clientPrice.amount ? formatMoney(order.clientPrice) :
                          (<span className={styles.emptyValue}/>)
                        }</td>
                      <td className={`${styles.td} ${styles.tdMuted}`}>
                        {formatMarginPercent(order.pricingMarginPercent) ?? <span className={styles.emptyValue}/>}
                      </td>
                      <td className={styles.td}>{order.promoCode ? (
                          <span className={styles.promoCodePill}>{promoCodeTrimmed}</span>
                        ) : (
                          <span className={styles.emptyValue}/>
                        )}</td>
                      <td className={styles.td}>{discountAmount > 0 ? formatMoney({ amount: discountAmount, currency: discountCurrency }) : (
                          <span className={styles.emptyValue}/>
                        )}</td>
                      <td className={`${styles.td} ${styles.tdMuted}`}>{order.margin.amount ? formatMoney(order.margin) : (
                          <span className={styles.emptyValue}/>
                        )}</td>
                      <td className={styles.td}>{
                        order.paidWithMams ? formatMoney({ amount: order.paidWithMams, currency: discountCurrency }) : (<span className={styles.emptyValue}/>)
                        }</td>
                      <td className={`${styles.td} ${styles.tdMuted}`}>
                        {order.refundedAmount?.amount
                          ? formatMoney(order.refundedAmount)
                          : <span className={styles.emptyValue} />}
                      </td>
                      <td className={styles.td}>{order.customerEmail ? order.customerEmail : (
                          <span className={styles.emptyValue}/>
                        )}
                      </td>
                      <td className={styles.td}>{formatDate(order.createdAt)}</td>
                      <td className={styles.td}>{order.paidAt ? formatDate(order.paidAt) : (
                          <span className={styles.emptyValue}/>
                        )}</td>
                      <td className={`${styles.tdLast} ${styles.stickyLast}`}
                        onClick={() => handleOpenOrderPanel(order)} >
                        <button
                          type="button"
                          className={styles.openOrderButton}
                          aria-label={`Open full details for order ${order.orderId}`}
                        >
                          <img src={expandIcon} alt="" aria-hidden className={styles.openIcon} />
                        </button>
                      </td>
                    </tr>
                    {isExpanded ? (
                      <tr key={`${String(order.orderId)}-details`}>
                        <td className={`${styles.td} ${styles.detailsCell}`} colSpan={19}>
                          <div className={styles.detailsWrapper}>
                            <table className={styles.nestedTable}>
                              <thead>
                                <tr>
                                  <th className={`${styles.nestedTh} ${styles.nestedThFirst} ${styles.nestedColExpand}`}></th>
                                  <th className={`${styles.nestedTh} ${styles.nestedThIndex} ${styles.nestedColId}`}>#</th>
                                  <th className={`${styles.nestedTh} ${styles.nestedColReference}`}>Ski Pass ID</th>
                                  <th className={`${styles.nestedTh} ${styles.nestedColPasses}`}>Type</th>
                                  <th className={`${styles.nestedTh} ${styles.nestedColSkiDays}`}>Ski Days</th>
                                  <th className={`${styles.nestedTh} ${styles.nestedColStartDate}`}>Start Date</th>
                                  <th className={`${styles.nestedTh} ${styles.nestedColDomain}`}>Domain (Ski Area)</th>
                                  <th className={styles.nestedTh}>Pass Price</th>
                                  <th className={styles.nestedTh}>Supplier Price</th>
                                  <th className={styles.nestedTh}>Pass-level Margin</th>
                                  <th className={styles.nestedTh}>Name (First, Last)</th>
                                  <th className={`${styles.nestedTh} ${styles.nestedThLast}`}>Birth Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {order.items.map((item: OrderItemDto, index) => {
                                  const isRefunded = String(order.status) === 'refunded';

                                  return (
                                    <tr key={`${order.orderId}-${item.skipassId}-${index}`} className={styles.nestedTr}>
                                      <td className={`${styles.nestedTd} ${styles.nestedColExpand}`}></td>
                                      <td className={`${styles.nestedTd} ${styles.nestedTdIndex} ${styles.nestedColId}`}>{index + 1}</td>
                                      <td className={`${styles.nestedTd} ${styles.nestedColReference}`}>{item.skipassId ?
                                        item.skipassId : ( <span className={styles.emptyValue}/>)}
                                      </td>
                                      <td className={`${styles.nestedTd} ${styles.nestedColPasses}`}>{item.ageCategory}</td>
                                      <td className={`${styles.nestedTd} ${styles.nestedColSkiDays}`}>{item.days}</td>
                                      <td className={`${styles.nestedTd} ${styles.nestedColStartDate}`}>{formatDateOnly(item.startDate)}</td>
                                      <td className={`${styles.nestedTd} ${styles.nestedColDomain}`}>{item.domain.name}</td>
                                      <td className={`${styles.nestedTd} ${isRefunded ? styles.refundedPrice : ''}`}>
                                        {isRefunded ? formatMoney(item.skiPassPrice) : (
                                          <span className={styles.emptyValue}/>
                                        )}
                                      </td>
                                      <td className={styles.nestedTd}>
                                        {item.supplierPrice.amount ? formatMoney(item.supplierPrice) : (
                                          <span className={styles.emptyValue}/>
                                        )}
                                      </td>
                                      <td className={styles.nestedTd}>
                                        {item.margin.amount ? formatMoney(item.margin) : (
                                          <span className={styles.emptyValue}/>
                                        )}
                                      </td>
                                      <td className={styles.nestedTd}>
                                        {item.firstName? `${item.firstName} ${item.lastName}` : (
                                          <span className={styles.emptyValue}/>
                                        )}
                                      </td>
                                      <td className={`${styles.nestedTd} ${styles.nestedTdLast}`}>
                                        {item.birthDate ? formatDateOnly(item.birthDate) : (
                                          <span className={styles.emptyValue}/>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {selectedOrder ? (
            <AdminOrderDetailsPanel
              order={selectedOrder}
              passCount={selectedOrderPassCount}
              statusLabel={selectedOrderStatusLabel}
              statusClassName={selectedOrderStatusClass}
              domainNames={selectedOrderDomainNames}
              onClose={handleCloseOrderPanel}
              onOrderCancelled={handleOrderCancelled}
            />
          ) : null}
          {rows.length === 0 ? (
            <div className={styles.noOrdersOverlay} style={{ left: overlayLeft }}>
              <div className={styles.noOrders}>
                <img src={emptyOrdersIcon} alt="No orders" className={styles.noOrdersIcon} />
                <span className={styles.noOrdersTitle}>No orders yet.</span><br />
                {hasActiveFilters ? (
                  <>
                    <span>Filters may be too restrictive</span><br/>
                    <button type="button" className={styles.noOrdersAction} onClick={clearAllFilters}>
                      Clear filters
                    </button>
                  </>
                ): null}
              </div>
            </div>
          ) : null}
          {copyToast.visible ? (
            <div
              className={styles.copyToast}
              style={{ left: copyToast.x, top: copyToast.y }}
              role="status"
              aria-live="polite"
            >
              Copied
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
